/**
 * EnterpriseIntegrations — public landing for forward-thinking real-
 * estate companies wanting to embed Central Rentals smart rails into
 * their core operations.
 *
 * Routes: /enterprise
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Cpu, Database, ShieldCheck, Workflow, Network, Loader2,
  Sparkles, ArrowRight, CheckCircle2, Calculator, FileText, Banknote, Briefcase,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const INTEGRATIONS = [
  { id: "embedded_pms",     icon: Workflow,    title: "Embedded PMS",                desc: "White-label our 13-province lease engine + tenant portal inside your existing app." },
  { id: "ai_api",            icon: Cpu,         title: "Maintenance + Retention API",  desc: "API access to our smart triage, anomaly detection, and tenant retention engines." },
  { id: "stripe_cad_api",    icon: Banknote,    title: "CAD Rent Tracking API",          desc: "CAD rent tracking with Interac reference matching and e-Transfer reconciliation." },
  { id: "tax_export_api",    icon: FileText,    title: "T776 / NR4 / T5013 Export",    desc: "Generate CRA-ready tax documents for your owners + investors via a single endpoint." },
  { id: "investor_portal",   icon: Briefcase,   title: "Investor Portal",              desc: "LP / GP distribution waterfalls + investor reporting — Canadian-tax-native." },
  { id: "screening_api",     icon: ShieldCheck, title: "Tenant Screening API",         desc: "Credit + ID + reference verification, fully PIPEDA-compliant." },
  { id: "rent_estimator",    icon: Calculator,  title: "Rent Estimator Widget",        desc: "Embed our market-rate widget on your listings or MLS to capture leads." },
  { id: "data_pipeline",     icon: Database,    title: "Two-way Data Pipeline",        desc: "Sync owners / properties / leases between Buildium, Yardi, AppFolio, and Central Rentals." },
];

function InquiryForm() {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", title: "", email: "", phone: "",
    company_size: "11-50", integration_kind: "embedded_pms", use_case: "",
    timeline: "this_quarter", budget_cad: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/enterprise/inquiry`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setDone(true);
      try {
        const g = await import("@/lib/google-tag");
        g.fireLeadFormSubmit({
          source: "enterprise_inquiry", email: form.email.trim(), valueCad: 5000,
          transactionId: d?.id || `ent_${Date.now()}`,
        });
      } catch { /* never block UX */ }
    } catch (e) { setError(String(e.message || e)); } finally { setBusy(false); }
  }

  if (done) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="enterprise-inquiry-success">
        <Sparkles className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
        <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Got it — we're on it.</h2>
        <p className="text-slate-700">Our team will personally reach out within 24 hours to scope your integration.</p>
        <p className="text-xs text-slate-500 mt-3">Confirmation also sent to {form.email}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="enterprise-inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Company *</label>
          <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="enterprise-company" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your name *</label>
          <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="enterprise-contact" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Title</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="enterprise-title" placeholder="VP Product, Broker-owner, CTO…" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email *</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enterprise-email" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone</label>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enterprise-phone" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Company size</label>
          <select value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })} data-testid="enterprise-size" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
            <option value="1-10">1–10</option>
            <option value="11-50">11–50</option>
            <option value="51-200">51–200</option>
            <option value="201-1000">201–1000</option>
            <option value="1000+">1000+</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Timeline</label>
          <select value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid="enterprise-timeline" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
            <option value="weeks">Within weeks</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
            <option value="exploring">Exploring</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Integration of interest *</label>
        <select value={form.integration_kind} onChange={(e) => setForm({ ...form, integration_kind: e.target.value })} data-testid="enterprise-integration" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
          {INTEGRATIONS.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
          <option value="other">Something else</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Use case *</label>
        <textarea required minLength={10} rows={4} value={form.use_case} onChange={(e) => setForm({ ...form, use_case: e.target.value })} data-testid="enterprise-usecase" placeholder="Tell us what you're trying to accomplish — the more specific, the faster we move." className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Budget (optional)</label>
        <input value={form.budget_cad} onChange={(e) => setForm({ ...form, budget_cad: e.target.value })} data-testid="enterprise-budget" placeholder="e.g. $50k pilot, $250k annual" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      {error && <div className="text-xs text-rose-600">{error}</div>}
      <button type="submit" disabled={busy} data-testid="enterprise-submit" className="w-full px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : <>Request a scoping call <ArrowRight className="w-4 h-4"/></>}
      </button>
      <p className="text-[11px] text-slate-500 text-center">Our team responds personally within 24 hours.</p>
    </form>
  );
}

export default function EnterpriseIntegrations() {
  useEffect(() => { document.title = "Enterprise Integrations — Central Rentals"; }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="enterprise-page">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="text-sm font-bold text-slate-900">← Central Rentals</Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-16">
        <section className="text-center pt-12 pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold mb-4">
            <Network className="w-3.5 h-3.5"/> ENTERPRISE INTEGRATIONS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 text-slate-900">
            Embed <span className="text-emerald-600">Canadian-native rails</span><br/>into your real-estate stack.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            For brokerages, REITs, insurance carriers, MLS boards, and proptech vendors who want Central Rentals' smart engines + tax-compliant CAD rails as part of their core platform — not just a side tool.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-16" data-testid="enterprise-integrations-grid">
          {INTEGRATIONS.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.id} data-testid={`enterprise-card-${it.id}`} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-emerald-400 hover:shadow-sm transition-all">
                <Icon className="w-7 h-7 text-emerald-600 mb-3"/>
                <div className="font-extrabold mb-1 text-slate-900">{it.title}</div>
                <div className="text-xs text-slate-600">{it.desc}</div>
              </div>
            );
          })}
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-extrabold mb-3 text-slate-900">Why us, why now</h2>
              <div className="space-y-3">
                {[
                  ["Canadian by default", "13-province lease law, T776/NR4/T5013 exports, Stripe CAD rails — not a US tool with maple syrup on top."],
                  ["Built API-first", "Every feature in our SaaS is exposed as a versioned, idempotent API. No surprises."],
                  ["Compose, don't compete", "We're a backend, not a brand. White-label, co-brand, or invisibly integrate — your call."],
                  ["Real engines, not buzzwords", "Smart Mark powers live triage, retention, and bookkeeping — not a chatbot demo."],
                ].map(([h, d]) => (
                  <div key={h} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5"/>
                    <div><div className="font-bold text-slate-900">{h}</div><div className="text-sm text-slate-600">{d}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">Already an enterprise customer?</div>
              <div className="text-sm text-emerald-900">Existing partners can manage API keys + usage in the admin dashboard. Talk to your account manager or write us at <a href="mailto:contact@centralrentals.ca" className="underline">contact@centralrentals.ca</a>.</div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 text-slate-900 p-6 sm:p-8 shadow-sm">
            <h2 className="text-2xl font-extrabold mb-1">Request a scoping call</h2>
            <p className="text-xs text-slate-500 mb-5">Tell us what you're building. We'll respond within 24 hours.</p>
            <InquiryForm/>
          </div>
        </section>
      </main>
    </div>
  );
}
